import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	createDateOnly,
	parseDateOnly,
	type DateOnly,
} from "@/shared/components/date-picker";
import { HelmetDexteel, MesfModal, TimeService } from "@dexteel/mesf-core";
import { Button, FormHelperText, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import moment from "moment-timezone";
import { useState } from "react";
import {
	Controller,
	FormProvider,
	useForm,
	useFormContext,
} from "react-hook-form";
import {
	type EmailCalendarFormValues,
	getSelectedRecipients,
	hasNonDefaultRecipients,
} from "./CalendarEmailForm";
import { EmailNotificationFields } from "./EmailNotificationFields";

interface EmailCalendarParams {
	selectedEmails: string[];
	publisherNote?: string;
	fromDate: DateOnly;
	toDate: DateOnly;
	addRecipientsToTopic: boolean;
}

interface EmailCalendarModalProps {
	open: boolean;
	loading: boolean;
	onClose: () => void;
	onConfirm: (params: EmailCalendarParams) => void;
}

const toMoment = (value: DateOnly | null): Moment | null => {
	const parts = parseDateOnly(value);
	if (parts === null) return null;

	return moment({
		year: parts.year,
		month: parts.month - 1,
		day: parts.day,
	});
};

const toDateOnly = (value: Moment | null): DateOnly | null => {
	if (value === null || !value.isValid()) return null;

	return createDateOnly({
		year: value.year(),
		month: value.month() + 1,
		day: value.date(),
	});
};

const getDefaultValues = (): EmailCalendarFormValues => {
	const serverTimeZone = TimeService.getInstance().getServerTimeZone();
	const currentMonth = moment().tz(serverTimeZone);
	return {
		notifyByEmail: true,
		selectedDefaultEmails: [],
		customEmails: [],
		publisherNote: "",
		fromDate: createDateOnly({
			year: currentMonth.year(),
			month: currentMonth.month() + 1,
			day: 1,
		}),
		toDate: createDateOnly({
			year: currentMonth.year(),
			month: currentMonth.month() + 1,
			day: currentMonth.date(),
		}),
	};
};

interface EmailDateFieldsProps {
	loading: boolean;
}

const EmailDateFields = ({ loading }: EmailDateFieldsProps) => {
	const { control, getValues, trigger } =
		useFormContext<EmailCalendarFormValues>();
	return (
		<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
			<Controller
				control={control}
				name="fromDate"
				rules={{
					required: "From and To dates are required.",
				}}
				render={({ field, fieldState }) => (
					<DatePicker
						label="From"
						value={toMoment(field.value)}
						onChange={(value) => {
							field.onChange(toDateOnly(value));
							void trigger("toDate");
						}}
						format="MM/DD/YYYY"
						disabled={loading}
						slotProps={{
							textField: {
								size: "small",
								fullWidth: true,
								error: Boolean(fieldState.error),
								helperText: fieldState.error?.message,
							},
						}}
					/>
				)}
			/>
			<Controller
				control={control}
				name="toDate"
				rules={{
					required: "From and To dates are required.",
					validate: (toDate) => {
						const fromDate = getValues("fromDate");
						return toDate === null || fromDate === null || fromDate <= toDate
							? true
							: "From date must be on or before To date.";
					},
				}}
				render={({ field, fieldState }) => (
					<DatePicker
						label="To"
						value={toMoment(field.value)}
						onChange={(value) => {
							field.onChange(toDateOnly(value));
							void trigger("fromDate");
						}}
						format="MM/DD/YYYY"
						disabled={loading}
						slotProps={{
							textField: {
								size: "small",
								fullWidth: true,
								error: Boolean(fieldState.error),
								helperText: fieldState.error?.message,
							},
						}}
					/>
				)}
			/>
		</Stack>
	);
};

const EmailCalendarFields = ({ loading }: EmailDateFieldsProps) => {
	return (
		<Stack spacing={2}>
			<Typography variant="body2" color="textSecondary">
				Send the latest published production events for the selected date range.
			</Typography>
			<EmailDateFields loading={loading} />
		</Stack>
	);
};

export const EmailCalendarModal = ({
	open,
	loading,
	onClose,
	onConfirm,
}: EmailCalendarModalProps) => {
	const methods = useForm<EmailCalendarFormValues>({
		defaultValues: getDefaultValues(),
		mode: "onChange",
	});
	const [recipientsReady, setRecipientsReady] = useState(false);
	const [pendingValues, setPendingValues] =
		useState<EmailCalendarFormValues | null>(null);

	const { formState, handleSubmit } = methods;
	// Read isValid on every render: react-hook-form only tracks it once it has
	// been read, and reading it after `!recipientsReady` in `disabled` skipped
	// that read while recipients loaded, leaving Send Email disabled for good.
	const { isValid } = formState;

	const submit = (
		values: EmailCalendarFormValues,
		addRecipientsToTopic: boolean,
	) => {
		if (values.fromDate === null || values.toDate === null) return;

		const trimmedNote = values.publisherNote.trim();
		onConfirm({
			selectedEmails: getSelectedRecipients(values),
			publisherNote: trimmedNote || undefined,
			fromDate: values.fromDate,
			toDate: values.toDate,
			addRecipientsToTopic,
		});
	};

	const requestSubmit = (values: EmailCalendarFormValues) => {
		if (hasNonDefaultRecipients(values)) {
			setPendingValues(values);
			return;
		}
		submit(values, false);
	};

	return (
		<FormProvider {...methods}>
			{open && <HelmetDexteel title="Email Calendar" />}
			<MesfModal
				maxWidth="sm"
				fullWidth
				open={open}
				handleClose={onClose}
				title="Email Calendar"
			>
				<MesfModal.Content dividers>
					<EmailCalendarFields loading={loading} />
					<EmailNotificationFields
						open={open}
						resource="CALENDAR_PUBLISH"
						loading={loading}
						required
						getDefaultValues={getDefaultValues}
						onReadyChange={setRecipientsReady}
					/>
					{recipientsReady && formState.errors.customEmails && (
						<FormHelperText error>
							Select or add at least one recipient.
						</FormHelperText>
					)}
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button onClick={onClose} disabled={loading}>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit(requestSubmit)}
						variant="contained"
						disabled={loading || !recipientsReady || !isValid}
						data-test-id="send-calendar-email-button"
					>
						{loading ? "Sending..." : "Send Email"}
					</Button>
				</MesfModal.Actions>
			</MesfModal>
			<ConfirmationDialogRaw
				title="Replace the topic recipients?"
				message="This list differs from the default recipients of the email topic. Do you want to replace the topic list with this one? The email is sent either way."
				open={pendingValues !== null}
				onClose={(value?: string) => {
					const values = pendingValues;
					setPendingValues(null);
					if (values) submit(values, value === "OK");
				}}
			/>
		</FormProvider>
	);
};
